// src/components/FeaturedContent.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getDoc, doc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase/config';
import PortalIcon from './ui/PortalIcon';

// Constants
const CONTENT_TYPES = {
  BLOG: 'blog',
  MATERIALS: 'materials', 
  BUNDLES: 'bundles'
};

const CONTENT_CONFIG = {
  [CONTENT_TYPES.BLOG]: {
    icon: 'edit',
    label: 'Featured Blog Posts',
    color: '#F09E07',
    path: '/blog'
  },
  [CONTENT_TYPES.MATERIALS]: {
    icon: 'book',
    label: 'Featured Study Materials',
    color: '#10b981',
    path: '/materials'
  },
  [CONTENT_TYPES.BUNDLES]: {
    icon: 'package',
    label: 'Featured Course Bundles',
    color: '#3b82f6',
    path: '/bundles'
  }
};

// Custom hooks
const useFeaturedContent = () => {
  const [featuredContent, setFeaturedContent] = useState({
    blog: { enabled: true, items: [] },
    materials: { enabled: true, items: [] },
    bundles: { enabled: true, items: [] }
  });
  const [contentDetails, setContentDetails] = useState({
    blog: [],
    materials: [],
    bundles: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeaturedContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);


      // Fetch featured content configuration
      const siteDoc = await getDoc(doc(db, 'siteContent', 'main'));
      let featuredConfig = null;
      
      if (siteDoc.exists()) {
        const siteData = siteDoc.data();
        if (siteData.featuredContent) {
          featuredConfig = siteData.featuredContent;
          setFeaturedContent(featuredConfig);
        } else {
        }
      } else {
      }

      // Fetch detailed content for each type
      const [blogSnap, materialsSnap, bundlesSnap] = await Promise.all([
        getDocs(collection(db, 'blogs')),
        getDocs(collection(db, 'materials')),
        getDocs(collection(db, 'bundles'))
      ]);

      const blogData = blogSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const materialsData = materialsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const bundlesData = bundlesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));


      setContentDetails({
        blog: blogData,
        materials: materialsData,
        bundles: bundlesData
      });

    } catch (err) {
      console.error('❌ Error fetching featured content:', err);
      setError(err.message);
      
      // Set fallback data if Firebase fails
      const fallbackData = {
        blog: {
          enabled: true,
          items: [
            { id: '1', title: 'Getting Started with React', description: 'Learn the basics of React development', url: '#', layout: 'card' },
            { id: '2', title: 'Advanced JavaScript Tips', description: 'Master advanced JavaScript concepts', url: '#', layout: 'card' },
            { id: '3', title: 'CSS Grid Layout Guide', description: 'Complete guide to CSS Grid', url: '#', layout: 'card' }
          ]
        },
        materials: {
          enabled: true,
          items: [
            { id: '1', title: 'React Cheat Sheet', description: 'Quick reference for React', url: '#', price: 'Free', layout: 'small-card' },
            { id: '2', title: 'JavaScript Notes', description: 'Comprehensive JS notes', url: '#', price: '$9.99', layout: 'small-card' }
          ]
        },
        bundles: {
          enabled: true,
          items: [
            { id: '1', title: 'Complete Web Development', description: 'Full stack development course', url: '#', price: '$49.99', layout: 'card' }
          ]
        }
      };
      
      setFeaturedContent(fallbackData);
      setContentDetails({
        blog: fallbackData.blog.items,
        materials: fallbackData.materials.items,
        bundles: fallbackData.bundles.items
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedContent();
  }, [fetchFeaturedContent]);

  return { featuredContent, contentDetails, loading, error, refetch: fetchFeaturedContent };
};

// Utility functions
const truncateText = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const getContentTypeConfig = (type) => {
  return CONTENT_CONFIG[type] || { icon: 'file', label: 'Featured Content', color: '#6366f1', path: '/' };
};

// Sub-components
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-gray-700 rounded w-1/4 mx-auto mb-4"></div>
    <div className="h-8 bg-gray-700 rounded w-1/2 mx-auto mb-4"></div>
    <div className="h-4 bg-gray-700 rounded w-3/4 mx-auto"></div>
  </div>
);

const ErrorMessage = ({ error, onRetry }) => (
  <div className="text-center py-12">
    <div className="text-red-400 mb-4">Error loading content</div>
    <p className="text-gray-400 mb-6">{error}</p>
    <button
      onClick={onRetry}
      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      Try Again
    </button>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-12">
    <div className="mb-4 flex justify-center"><PortalIcon name="book" size={48} /></div>
    <h3 className="text-2xl font-bold text-white mb-4">No Featured Content Yet</h3>
    <p className="text-gray-400">Admin will add featured content soon!</p>
  </div>
);

const ContentCard = ({ item, config, index }) => {
  try {
    
    const handleClick = () => {
      if (item.url) {
        window.location.href = item.url;
      }
    };

    const truncateText = (text, maxLength) => {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const layout = item.layout || 'card';
    
    const cardStyles = {
      base: {
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        animation: `fadeInUp 0.6s ease forwards`,
        animationDelay: `${index * 0.1}s`,
        opacity: 0
      },
      hover: {
        transform: 'translateY(-2px)',
        background: 'rgba(255,255,255,0.08)',
        borderColor: config.color,
        boxShadow: `0 12px 30px ${config.color}25`
      }
    };

  if (layout === 'button') {
    return (
      <button
        onClick={handleClick}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '12px 16px',
          borderRadius: '8px',
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff',
          fontWeight: '500',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          animationDelay: `${index * 0.1}s`,
          opacity: 0,
          animation: `fadeInUp 0.6s ease forwards`
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
        }}
      >
        <PortalIcon name={config.icon} size={16} style={{ marginRight:8 }} />
        {truncateText(item.title || 'Untitled', 30)}
      </button>
    );
  }

  if (layout === 'small-card') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...cardStyles.base,
          padding: '16px',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          e.currentTarget.style.borderColor = config.color;
          e.currentTarget.style.boxShadow = `0 12px 30px ${config.color}25`;
        }}
        onMouseLeave={(e) => {
          Object.assign(e.currentTarget.style, cardStyles.base);
        }}
      >
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          {item.thumbnail && (
            <img
              src={item.thumbnail}
              alt={item.title}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                objectFit: 'cover',
                flexShrink: 0,
                border: '1px solid rgba(255,255,255,0.2)'
              }}
              loading="lazy"
            />
          )}
          <div style={{
            flex: 1,
            minWidth: 0
          }}>
            <h4 style={{
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '16px',
              marginBottom: '8px',
              margin: '0 0 8px 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {truncateText(item.title || 'Untitled', 45)}
            </h4>
            <p style={{
              color: '#94a3b8',
              fontSize: '14px',
              marginBottom: '12px',
              margin: '0 0 12px 0',
              lineHeight: '1.4',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {truncateText(item.description || 'No description available', 100)}
            </p>
            {item.price && (
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: config.color,
                  background: `${config.color}15`
                }}
              >
                {item.price}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default card layout
  return (
    <div
      onClick={handleClick}
      className="p-5 rounded-xl hover:transform hover:-translate-y-1 transition-all duration-300"
      style={cardStyles.base}
      onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardStyles.hover)}
      onMouseLeave={(e) => Object.assign(e.currentTarget.style, cardStyles.base)}
    >
      <div className="flex gap-4 items-start">
        {item.thumbnail && (
          <img
            src={item.thumbnail}
            alt={item.title}
            className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border border-white/20"
            loading="lazy"
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-bold text-base mb-2 truncate">
            {truncateText(item.title || 'Untitled', 45)}
          </h4>
          <p className="text-gray-400 text-sm mb-3 line-clamp-2">
            {truncateText(item.description || 'No description available', 100)}
          </p>
          {item.price && (
            <div
              className="inline-block px-3 py-1 rounded-md text-sm font-bold"
              style={{
                color: config.color,
                background: `${config.color}15`
              }}
            >
              {item.price}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  } catch (error) {
    console.error('❌ ContentCard error:', error);
    return (
      <div style={{
        padding: '16px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px',
        color: '#fff'
      }}>
        <h4 style={{ margin: '0 0 8px 0' }}>
          {item.title || 'Untitled'}
        </h4>
        <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>
          Error loading content
        </p>
      </div>
    );
  }
};

const ContentSection = ({ contentType, items, index }) => {
  const config = getContentTypeConfig(contentType);
  
  if (!items || items.length === 0) return null;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
        transition: 'all 0.3s ease',
        animationDelay: `${index * 0.1}s`,
        opacity: 0,
        animation: `fadeInUp 0.8s ease forwards`
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            background: `linear-gradient(135deg, ${config.color}, ${config.color}dd)`,
            boxShadow: `0 8px 20px ${config.color}33`
          }}
        >
          <PortalIcon name={config.icon} size={24} />
        </div>
        <div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#fff',
            margin: '0'
          }}>{config.label}</h3>
          <p style={{
            fontSize: '14px',
            color: '#94a3b8',
            margin: '0'
          }}>Handpicked resources for your success</p>
        </div>
      </div>
      
      <div
        style={{
          display: 'grid',
          gap: '16px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
        }}
      >        {items.map((item, itemIndex) => {          return (
            <ContentCard
              key={item.id}
              item={item}
              config={config}
              index={itemIndex}
            />
          );
        })}
      </div>

      <div style={{
        marginTop: '24px',
        textAlign: 'center'
      }}>
        <button
          onClick={() => window.location.href = config.path}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            borderRadius: '12px',
            fontWeight: '600',
            fontSize: '16px',
            background: `linear-gradient(135deg, ${config.color}, ${config.color}dd)`,
            color: '#000',
            border: 'none',
            cursor: 'pointer',
            boxShadow: `0 8px 20px ${config.color}33`,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = `0 12px 30px ${config.color}50`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = `0 8px 20px ${config.color}33`;
          }}
        >
          View All →
        </button>
      </div>
    </div>
  );
};

const FeaturedContent = () => {
  try {
    const { featuredContent, contentDetails, loading, error, refetch } = useFeaturedContent();

    const getFeaturedItems = useCallback((contentType) => {
      try {
        const content = featuredContent[contentType];
        
        if (!content || !content.enabled) {
          return [];
        }
        
        const items = content.items || [];
        
        const validItems = items
          .filter(item => item && item.id)
          .slice(0, 3);
        
        return validItems;
      } catch (err) {
        return [];
      }
    }, [featuredContent]);

    const contentSections = useMemo(() => {
      try {
        const sections = ['blog', 'materials', 'bundles']
          .map(type => {
            const items = getFeaturedItems(type);
            return {
              type,
              items
            };
          })
          .filter(section => {
            const hasItems = section.items.length > 0;
            return hasItems;
          });
        
        return sections;
      } catch (err) {
        return [];
      }
    }, [getFeaturedItems]);

    const hasAnyContent = contentSections.length > 0;
    

    if (loading) {
      return (
        <section style={{
          padding: '80px 20px',
          background: 'linear-gradient(to bottom, #1a1830, #0f0e1a)',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ color: '#fff' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #fff',
                borderTop: '4px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}></div>
              <p>Loading featured content...</p>
            </div>
          </div>
        </section>
      );
    }

    if (error) {
      return (
        <section style={{
          padding: '80px 20px',
          background: 'linear-gradient(to bottom, #1a1830, #0f0e1a)',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ color: '#fff' }}>
              <p style={{ color: '#ef4444' }}>Error loading featured content: {error}</p>
              <button 
                onClick={refetch}
                style={{
                  marginTop: '16px',
                  padding: '12px 24px',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </section>
      );
    }

    if (!hasAnyContent) {
      return null;
    }

    return (
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(to bottom, #1a1830, #0f0e1a)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              color: '#fff',
              marginBottom: '16px'
            }}>
              Handpicked Resources for You
            </h2>
            <p style={{
              fontSize: '1.2rem',
              color: '#94a3b8'
            }}>
              Discover our featured educational content
            </p>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '64px' }}>
            {contentSections.map((section, index) => (
              <ContentSection
                key={section.type}
                contentType={section.type}
                items={section.items}
                index={index}
              />
            ))}
          </div>
        </div>

        <style jsx>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </section>
    );
  } catch (error) {
    console.error('FeaturedContent component error:', error);
    return (
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(to bottom, #1a1830, #0f0e1a)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#fff',
            marginBottom: '16px'
          }}>
            Featured Content
          </h2>
          <p style={{
            fontSize: '1.2rem',
            color: '#94a3b8',
            marginBottom: '32px'
          }}>
            Loading...
          </p>
        </div>
      </section>
    );
  }
};

export default FeaturedContent;
